import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  theme,
} from 'antd';
import dayjs from 'dayjs';

import TemplateTaskDialog from '@totem/components/ticketing/templateManagement/details/TemplateTaskDialog';
import TemplateTasksTable, {
  DropPosition,
} from '@totem/components/ticketing/templateManagement/details/TemplateTasksTable';
import {
  Template,
  TemplateTask,
} from '@totem/components/ticketing/templateManagement/types';
import { TASK_TYPE_GROUP } from '@totem/components/ticketing/ticketing';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortNumberAscending } from '@totem/utilities/tableUtilities';

import '../../ticketing.css';

type TemplateFormValues = {
  name: string;
  ticketType: string;
  category: string;
  subCategory: string;
  services: string[];
  active: boolean;
  autoApply: boolean;
};

type TemplateTaskPositionUpdate = {
  id: string;
  parent: number;
  position: number;
};

const TemplateManagementDetailsPage = () => {
  const { token } = theme.useToken();
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { getApiToken, userProfile } = useContext(UserProfileContext);
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TemplateTask | null>(null);
  const [templateForm] = Form.useForm<TemplateFormValues>();

  const fetchTemplate = useCallback(() => {
    if (!templateId) {
      return;
    }
    setLoading(true);

    fetch(`${TICKETS_ENDPOINT}/templates/${templateId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: Template) => {
        if (isNotNull(result)) {
          setTemplate({ ...result, tasks: result.tasks || [] });
        }
      })
      .finally(() => setLoading(false));
  }, [getApiToken, templateId]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  useEffect(() => {
    if (template) {
      templateForm.setFieldsValue({
        name: template.name,
        ticketType: template.ticketType,
        category: template.category,
        subCategory: template.subCategory,
        services: template.services,
        active: template.active,
        autoApply: template.autoApply,
      });
    }
  }, [template, templateForm]);

  const handleTemplateSave = async () => {
    if (!template) {
      return;
    }
    const values = await templateForm.validateFields();
    const now = dayjs().toISOString();
    const payload: Template = {
      ...template,
      name: values.name,
      ticketType: values.ticketType,
      category: values.category,
      subCategory: values.subCategory,
      services: values.services || [],
      active: values.active,
      autoApply: values.autoApply,
      updatedByName: userProfile.email,
      updatedById: userProfile.id,
      updatedAt: now,
    };

    fetch(`${TICKETS_ENDPOINT}/templates/${template.id}`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    }).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        setTemplate(payload);

        setShowEditTemplateModal(false);
      }
    });
  };

  const handleTaskSave = (payload: TemplateTask, isEdit: boolean) => {
    if (!template) {
      return;
    }
    const url = isEdit
      ? `${TICKETS_ENDPOINT}/templates/${template.id}/tasks/${payload.id}`
      : `${TICKETS_ENDPOINT}/templates/${template.id}/tasks`;
    const method = isEdit ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    }).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        setShowTaskModal(false);

        setEditingTask(null);

        fetchTemplate();
      }
    });
  };

  const handleTaskDelete = (task: TemplateTask) => {
    if (!template) {
      return;
    }

    Modal.confirm({
      title: 'Delete Task',
      content: `Remove "${task.name}" from this template?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        fetch(`${TICKETS_ENDPOINT}/templates/${template.id}/tasks/${task.id}`, {
          method: 'DELETE',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
        }).then((res) => {
          const success = CheckResponseShowError(res);
          if (success) {
            fetchTemplate();
          }
        });
      },
    });
  };

  const applyTaskPositionUpdates = async (
    updates: TemplateTaskPositionUpdate[],
  ) => {
    if (!template || updates.length === 0) {
      return;
    }

    const updatedTasks = template.tasks.map((task) => {
      const update = updates.find((item) => item.id === task.id);
      if (!update) {
        return task;
      }
      return {
        ...task,
        parent: update.parent,
        position: update.position,
      };
    });

    const results = await Promise.all(
      updates.map((update) => {
        const updatedTask = updatedTasks.find((task) => task.id === update.id);
        if (!updatedTask) {
          return Promise.resolve(false);
        }
        return fetch(
          `${TICKETS_ENDPOINT}/templates/${template.id}/tasks/${update.id}`,
          {
            method: 'PUT',
            headers: new Headers({
              Authorization: `Bearer ${getApiToken()}`,
            }),
            body: JSON.stringify(updatedTask),
          },
        ).then((res) => CheckResponseShowError(res));
      }),
    );

    if (results.every(Boolean)) {
      setTemplate({ ...template, tasks: updatedTasks });
    } else {
      fetchTemplate();
    }
  };

  const handleMoveTask = (
    draggedTaskNumber: number,
    targetTaskNumber: number,
    position: DropPosition,
  ) => {
    if (!template) {
      return;
    }

    const draggedTask = template.tasks.find(
      (task) => task.number === draggedTaskNumber,
    );
    const targetTask = template.tasks.find(
      (task) => task.number === targetTaskNumber,
    );

    if (!draggedTask || !targetTask || draggedTask.id === targetTask.id) {
      return;
    }

    if (position === 'inside' && targetTask.type !== TASK_TYPE_GROUP) {
      return;
    }

    const getTaskByNumber = (number: number) =>
      template.tasks.find((task) => task.number === number);

    let parentWalker =
      position === 'inside' ? targetTask.number : targetTask.parent;
    while (parentWalker !== 0) {
      if (parentWalker === draggedTask.number) {
        return;
      }
      const parentTask = getTaskByNumber(parentWalker);
      if (!parentTask) {
        break;
      }
      parentWalker = parentTask.parent;
    }

    const destinationParent =
      position === 'inside' ? targetTask.number : targetTask.parent;

    const destinationSiblings = template.tasks
      .filter(
        (task) =>
          task.parent === destinationParent && task.id !== draggedTask.id,
      )
      .sort((compA, compB) =>
        sortNumberAscending(compA.position, compB.position),
      );

    const targetIndex = destinationSiblings.findIndex(
      (task) => task.number === targetTask.number,
    );

    let insertIndex = destinationSiblings.length;
    if (position === 'above' && targetIndex >= 0) {
      insertIndex = targetIndex;
    } else if (position === 'below' && targetIndex >= 0) {
      insertIndex = targetIndex + 1;
    }

    const destinationWithDragged = [...destinationSiblings];
    destinationWithDragged.splice(insertIndex, 0, {
      ...draggedTask,
      parent: destinationParent,
    });

    const resequence = (taskList: TemplateTask[], parent: number) =>
      taskList.map((task, idx) => ({
        id: task.id,
        parent,
        position: idx,
      }));

    const updates: TemplateTaskPositionUpdate[] = [];
    updates.push(...resequence(destinationWithDragged, destinationParent));

    if (draggedTask.parent !== destinationParent) {
      const sourceSiblings = template.tasks
        .filter(
          (task) =>
            task.parent === draggedTask.parent && task.id !== draggedTask.id,
        )
        .sort((compA, compB) =>
          sortNumberAscending(compA.position, compB.position),
        );
      updates.push(...resequence(sourceSiblings, draggedTask.parent));
    }

    applyTaskPositionUpdates(updates);
  };

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title={
          template
            ? `Template: ${template.category} / ${template.subCategory}`
            : 'Template Details'
        }
        extra={
          <>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/dashboard/ticketingTemplates')}
            >
              Back
            </Button>
            &nbsp;&nbsp;&nbsp;
            <RecordCountBadge count={template?.tasks?.length || 0} />
            &nbsp;&nbsp;&nbsp;
            <Button
              icon={<EditOutlined />}
              onClick={() => setShowEditTemplateModal(true)}
              disabled={!template}
            >
              Edit Template
            </Button>
            &nbsp;&nbsp;&nbsp;
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTask(null);

                setShowTaskModal(true);
              }}
              disabled={!template}
            >
              New Task
            </Button>
          </>
        }
      >
        {loading && <div>Loading template details...</div>}
        {!loading && template && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Name" span={2}>
                {template.name}
              </Descriptions.Item>
              <Descriptions.Item label="Ticket Type" span={2}>
                {template.ticketType}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {template.category}
              </Descriptions.Item>
              <Descriptions.Item label="Sub Category">
                {template.subCategory}
              </Descriptions.Item>
              <Descriptions.Item label="Services" span={2}>
                {template.services && template.services.length > 0
                  ? template.services.join(', ')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Active">
                {template.active ? (
                  <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                ) : (
                  <MinusCircleOutlined
                    style={{ color: token.colorTextDisabled }}
                  />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Auto Apply">
                {template.autoApply ? (
                  <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                ) : (
                  <MinusCircleOutlined
                    style={{ color: token.colorTextDisabled }}
                  />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {template.createdByName}
              </Descriptions.Item>
              <Descriptions.Item label="Updated By">
                {template.updatedByName}
              </Descriptions.Item>
            </Descriptions>
            <br />
            <TemplateTasksTable
              tasks={template.tasks || []}
              onMoveTask={handleMoveTask}
              onEditTask={(task) => {
                setEditingTask(task);

                setShowTaskModal(true);
              }}
              onDeleteTask={handleTaskDelete}
            />
          </>
        )}
      </IBCard>
      {template && (
        <Modal
          title="Edit Template"
          open={showEditTemplateModal}
          onCancel={() => setShowEditTemplateModal(false)}
          onOk={handleTemplateSave}
          okText="Save"
        >
          <Form layout="vertical" form={templateForm}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Name is required.' }]}
            >
              <Input placeholder="Name" />
            </Form.Item>
            <Form.Item label="Ticket Type" name="ticketType">
              <Input placeholder="Ticket Type" />
            </Form.Item>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: 'Category is required.' }]}
            >
              <Input placeholder="Category" />
            </Form.Item>
            <Form.Item
              label="Sub Category"
              name="subCategory"
              rules={[{ required: true, message: 'Sub category is required.' }]}
            >
              <Input placeholder="Sub category" />
            </Form.Item>
            <Form.Item label="Services" name="services">
              <Select
                mode="tags"
                placeholder="Add service tags"
                tokenSeparators={[',']}
              />
            </Form.Item>
            <Form.Item label="Active" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="Auto Apply"
              name="autoApply"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      )}

      {template && (
        <TemplateTaskDialog
          open={showTaskModal}
          template={template}
          task={editingTask}
          createdByName={userProfile.email}
          createdById={userProfile.id}
          onCancel={() => {
            setShowTaskModal(false);

            setEditingTask(null);
          }}
          onSave={handleTaskSave}
        />
      )}
    </ContentLayout>
  );
};

export default TemplateManagementDetailsPage;
